/**
 * AppBrowsePage — the browse home (/registry), NETWORK-REGISTRY.md §9.
 *
 * Tabs on the shared ``CatalogBrowser`` (inline/page mode):
 *   • **Registry** (everyone) — RELEASED packages; primary action **Deploy** → the
 *     deploy wizard that ends in a room. A first-class "Build new" tile → create flow.
 *   • **Networks** (everyone) — the caller's own networks (from the shell via
 *     outletContext); a source dropdown filters All / Manually-created /
 *     Registry-deployed; per-tile ⋮ menu offers Open + (registry-deployed) a lazy
 *     upgrade check → Upgrade.
 *   • **Review** / **Archived** (ADMIN + dev only) — DRAFT / ARCHIVED packages
 *     awaiting curation; gated by the backend ``canCurate`` probe.
 *
 * Data via ``networkRegistryApi``. Right-panel detail lazy-loads per tile.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Search, SlidersHorizontal, X as XIcon, ChevronDown } from 'lucide-react';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { useToast } from '@stepflow/ui-shell/context/ToastContext.jsx';
import CatalogBrowser, { TileActionsMenu } from '@stepflow/ui-shared/components/agentTools/CatalogBrowser.jsx';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import { getPinnedSet, togglePinned } from '@stepflow/ui-shared/utils/pinnedNetworks.js';
import NetworkDashboardGrid from './components/dashboard/NetworkDashboardGrid.jsx';
import RoomLandingGrid from './components/dashboard/RoomLandingGrid.jsx';
import NetworkAvatar from '@stepflow/ui-shared/components/dashboard/NetworkAvatar.jsx';
import { useDashboardData } from '@stepflow/ui-shared/hooks/useDashboardData.js';
import { getMyRoomsVitals } from '@stepflow/ui-shared/services/networksDashboardApi.js';
import ConfirmDialog from '@stepflow/ui-shared/components/ConfirmDialog.jsx';
import {
  listRegistryPackages,
  listRegistryDrafts,
  listRegistryArchived,
  approveRegistryPackage,
  archiveRegistryPackage,
  reviewRegistryPackage,
  deleteRegistryPackage,
  getRegistryCurationCapability,
  getRegistryPackage,
  getRegistryConfigStatus,
  listRegistryPackageVersions,
  upgradeNetworkFromRegistry,
} from '@stepflow/ui-shared/services/networkRegistryApi.js';
import RecentlyVisited from './components/RecentlyVisited.jsx';
import NetworkDeployFlow from './components/NetworkDeployFlow.jsx';
import NetworkToolConfigPanel from './components/NetworkToolConfigPanel.jsx';
import ReleaseNetworkDialog from './components/ReleaseNetworkDialog.jsx';
import PackageDetailView from './components/PackageDetailView.jsx';

const BUILD_NEW_ID = '__build_new__';

// Whether the Review (curation) tab is shown is decided by the BACKEND
// (`canCurate` — true only in the curation env, dev, for a curation role). The FE
// carries NO environment literal; it just asks and obeys.

// Leading identity avatar for a registry catalog tile — the SAME hexagon a
// registry-deployed network shows on the dashboard, seeded by the package's
// portable ``visualSeed`` (falls back to the registryNetworkId), so a package
// looks identical in Review / Archive / detail and after deployment. The
// "+ Build new" entry (no ``_pkg``) renders nothing.
function renderRegistryLeading(entry) {
  const p = entry && entry._pkg;
  if (!p) return null;
  const seed = p.visualSeed || p.registryNetworkId || '';
  if (!seed) return null;
  return (
    <NetworkAvatar
      networkId={p.registryNetworkId || seed}
      name={p.title}
      seed={seed}
      shape="hexagon"
      size={26}
      className="mt-0.5"
    />
  );
}

function packageToEntry(p) {
  const label = p.title || p.registryNetworkId || 'Untitled';
  const ver = p.stepflowVersion || (p.version ? `v${p.version}` : '');
  return {
    id: String(p.registryNetworkId || ''),
    label,
    description: p.summary || '',
    badge: [p.domain, ver, `${p.agentCount || 0} agents`, `${p.skillCount || 0} skills`].filter(Boolean).join(' · '),
    _pkg: p,
  };
}

// A network is "registry-deployed" iff it carries the registry provenance the
// deploy orchestrator stamps. Manual (workbench-created) networks lack it.
function isRegistryNetwork(n) {
  return Boolean(n && n.sourceRegistryId);
}

function networkToEntry(n) {
  const registry = isRegistryNetwork(n);
  // ONLY the semantic version label (e.g. "1.00", "1.10", "2.00") — NEVER the raw
  // registry integer. Absent (older deploys predate the stamp) → show no version,
  // not a fabricated "v1".
  const ver = n.sourceStepflowVersion || '';
  return {
    id: String(n.networkId || ''),
    label: n.name || n.networkId || 'Untitled',
    description: '',
    badge: [registry ? 'Registry-deployed' : 'Manual', registry ? ver : null, n.status]
      .filter(Boolean).join(' · '),
    _net: n,
  };
}


// A version picker for a registry network. Shows the current (default: latest)
// stepflowVersion as a small chip; on open it lazy-loads all RELEASED versions
// (newest first) so the user can read/deploy an older one. Registry integers are
// the ordering key — only the semantic ``stepflowVersion`` is ever shown.
function VersionSelect({ registryNetworkId, currentVersion, currentLabel, onPick, testidPrefix, size = 'chip' }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState(null); // null = unloaded
  const [loading, setLoading] = useState(false);

  const loadVersions = useCallback(async () => {
    if (versions) return;
    setLoading(true);
    try {
      const res = await listRegistryPackageVersions(registryNetworkId);
      const all = Array.isArray(res?.versions) ? res.versions : [];
      // A browser can only read/deploy RELEASED versions; newest first.
      const released = all
        .filter((v) => String(v.status || '') === 'RELEASED')
        .sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0));
      setVersions(released);
    } catch {
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [registryNetworkId, versions]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open) loadVersions();
    setOpen((v) => !v);
  };

  const label = currentLabel || (currentVersion ? `v${currentVersion}` : 'version');
  const btnCls = size === 'lg'
    ? 'inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50'
    : 'inline-flex items-center gap-0.5 rounded-md border border-gray-200 bg-white/90 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50';
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        data-testid={`${testidPrefix}-version-toggle-${registryNetworkId}`}
        aria-label="Choose version"
        title="Choose version"
        className={btnCls}
      >
        {label}
        <ChevronDown className={size === 'lg' ? 'h-3.5 w-3.5' : 'h-3 w-3'} aria-hidden />
      </button>
      <PortalMenu anchorRef={btnRef} open={open} onClose={() => setOpen(false)} align="bottom-right" width={170}>
        <div className="py-1" data-testid={`${testidPrefix}-version-menu-${registryNetworkId}`}>
          {loading ? (
            <div className="px-3 py-1.5 text-[11px] text-gray-400">Loading…</div>
          ) : !versions || versions.length === 0 ? (
            <div className="px-3 py-1.5 text-[11px] text-gray-400">No other versions</div>
          ) : (
            versions.map((v, i) => (
              <button
                key={v.version}
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(false); onPick(v); }}
                data-testid={`${testidPrefix}-version-${registryNetworkId}-${v.version}`}
                className={`block w-full px-3 py-1.5 text-left text-[11px] hover:bg-gray-50 ${
                  Number(v.version) === Number(currentVersion) ? 'font-medium text-blue-700' : 'text-gray-700'
                }`}
              >
                {v.stepflowVersion || `v${v.version}`}{i === 0 ? ' · latest' : ''}
              </button>
            ))
          )}
        </div>
      </PortalMenu>
    </>
  );
}

export default function AppBrowsePage() {
  const navigate = useNavigate();
  const { activeTenantId } = useAuth();   // deploy wizard + the live dashboard beacon (tenant context channel)
  const { showToast } = useToast();

  const [tab, setTab] = useState('networks'); // default; 'registry' | 'networks' | 'rooms' | 'review' | 'archived'
  const [showReview, setShowReview] = useState(false); // backend-decided (canCurate)
  const [packages, setPackages] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [deployTarget, setDeployTarget] = useState(null);
  const [approvingId, setApprovingId] = useState('');
  const [busyId, setBusyId] = useState('');  // tile-action (archive/review/delete) in flight
  const [deleteTarget, setDeleteTarget] = useState(null);  // package pending delete-confirm
  const [openTarget, setOpenTarget] = useState(null);  // package shown as the standalone detail page
  // Per-network chosen version (registry tiles). Absent → the tile's own (latest)
  // version. { [registryNetworkId]: { version, stepflowVersion } }.
  const [pickedVersion, setPickedVersion] = useState({});
  const EMPTY_FILTERS = { awsAccountId: '', orgId: '', tenantId: '', userId: '', sourceNetworkId: '' };
  // Review-tab provenance filters — all default to '' (= all). AND-combined
  // server-side. `filters` is the DRAFT edited inside the advanced-search
  // dropdown; `appliedFilters` is what actually drives `load` (committed via the
  // dropdown's Search button). Split so keystrokes don't refetch on every char.
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterBtnRef = useRef(null);
  // Catalog search — lives in the tab bar (right side); the CatalogBrowser's own
  // search bar is suppressed so we don't waste a whole strip on it.
  const [search, setSearch] = useState('');

  // --- Networks tab (unrestricted): the caller's own networks, from the shell. ---
  const ctx = useOutletContext() || {};
  const networks = useMemo(() => (Array.isArray(ctx.networks) ? ctx.networks : []), [ctx.networks]);
  const refreshNetworks = typeof ctx.refreshNetworks === 'function' ? ctx.refreshNetworks : null;
  // The caller's ROOM memberships (from GET /my/agentic-networks) — agnostic of
  // network membership, so it drives the "Rooms" landing for room-only users.
  const agenticRooms = useMemo(() => (Array.isArray(ctx.agenticRooms) ? ctx.agenticRooms : []), [ctx.agenticRooms]);
  // 'active' (rooms>0) is the default view — the networks actually in use. Full set:
  // 'all' | 'active' | 'deployed' | 'pinned' | 'manual' | 'registry'.
  const [sourceFilter, setSourceFilter] = useState('active');
  const [sourceMenuOpen, setSourceMenuOpen] = useState(false);
  const sourceBtnRef = useRef(null);
  // Per-device ★ pinned set — a personal filter (localStorage), not a subscription.
  // A bump counter forces re-filter when the user pins/unpins from a tile ⋮ menu.
  const [pinnedTick, setPinnedTick] = useState(0);
  const pinnedSet = useMemo(() => getPinnedSet(), [pinnedTick]);
  const togglePin = useCallback((nid) => {
    togglePinned(nid);
    setPinnedTick((t) => t + 1);
  }, []);
  // Lazy per-network upgrade check: { [networkId]: {checking?, latestVersion, latestLabel, available} }.
  const [upgradeInfo, setUpgradeInfo] = useState({});
  const [upgradeTarget, setUpgradeTarget] = useState(null);  // network pending upgrade-confirm
  // Per-network config completeness (§14): { [networkId]: { state:'green'|'yellow'|'unknown', ... } }.
  // Derived live from the reconcile endpoint; drives tile tone + the "config missing" note.
  const [configStatus, setConfigStatus] = useState({});
  // Single-pane connector/room management reachable from the tile ⋮ menu.
  const [toolConfigTarget, setToolConfigTarget] = useState(null);  // network being configured (network scope)
  const [createRoomTarget, setCreateRoomTarget] = useState(null);  // network to open a room for

  // Review + Archived share the same curation gate (dev-env + curation role).
  const reviewing = tab === 'review' && showReview;
  const archiving = tab === 'archived' && showReview;
  const networking = tab === 'networks';
  const rooming = tab === 'rooms';
  const curating = reviewing || archiving; // any admin-gated feed (filters apply to both)

  // Rich live vitals for the Networks tile grid (LANDING). Push-driven: initial
  // GET + a coarse ROOM_ACTIVITY_CHANGED beacon on the TENANT context channel
  // (every member sees runs light up) + a tab-focus refetch fallback — no polling.
  // Only active on the Networks tab.
  const {
    networks: dashVitals, loading: dashLoading, error: dashError, livePing,
  } = useDashboardData(networking ? activeTenantId : undefined);

  // "My Rooms" landing vitals — the caller's room memberships (agenticRooms) enriched
  // with per-room vitals (runs/success/consumption) via getMyRoomsVitals, which fans a
  // rooms-dashboard call per distinct network. Fetched lazily: only when the Rooms tab
  // is active, and re-run when the membership list changes (roster push refetches it in
  // the shell). Degrades to bare membership rows if a network's vitals call fails.
  const [roomVitals, setRoomVitals] = useState([]);
  const [roomVitalsLoading, setRoomVitalsLoading] = useState(false);
  const [roomVitalsError, setRoomVitalsError] = useState(null);
  // A stable key over (networkId,poolId) pairs so the effect only re-fires when the
  // actual membership set changes — not on every shell re-render / array identity.
  const roomsKey = useMemo(
    () => agenticRooms.map((r) => `${r.networkId}:${r.poolId}`).sort().join('|'),
    [agenticRooms],
  );
  useEffect(() => {
    if (!rooming) return undefined;
    if (!agenticRooms.length) { setRoomVitals([]); return undefined; }
    let alive = true;
    setRoomVitalsLoading(true);
    setRoomVitalsError(null);
    getMyRoomsVitals(agenticRooms)
      .then((rows) => { if (alive) setRoomVitals(rows); })
      .catch((e) => { if (alive) setRoomVitalsError(e?.message || 'Failed to load rooms'); })
      .finally(() => { if (alive) setRoomVitalsLoading(false); });
    return () => { alive = false; };
    // roomsKey captures the membership set; rooming gates the fetch to the tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooming, roomsKey]);

  // Vitals filtered by the source toggle (manual/registry), keyed off the shell
  // network row (which carries sourceRegistryId). Falls back to showing all when
  // a vitals row has no matching shell row.
  const dashVitalsFiltered = useMemo(() => {
    const bySource = (v) => {
      const nid = v.networkId;
      if (sourceFilter === 'pinned') return pinnedSet.has(nid);
      // Room-lifecycle axis: Active = has ≥1 room; Deployed = created, 0 rooms.
      if (sourceFilter === 'active') return Number(v.roomCount || 0) > 0;
      if (sourceFilter === 'deployed') return Number(v.roomCount || 0) === 0;
      const shell = networks.find((n) => n.networkId === nid);
      const isReg = Boolean(shell && shell.sourceRegistryId);
      if (sourceFilter === 'manual') return !isReg;
      if (sourceFilter === 'registry') return isReg;
      return true;  // 'all'
    };
    // For the ACTIVE view with a sparse set (≤3), the grid also shows the Deployed
    // ones below a divider — so it needs the full vitals, not just the active slice.
    // We hand it everything for active/all; other filters get the strict slice.
    if (sourceFilter === 'active' || sourceFilter === 'all') {
      return (dashVitals || []);
    }
    return (dashVitals || []).filter(bySource);
  }, [dashVitals, networks, sourceFilter, pinnedSet]);

  // Ask the backend whether to show the Review/Archived tabs (dev-env + curation role).
  useEffect(() => {
    let alive = true;
    getRegistryCurationCapability()
      .then((res) => { if (alive) setShowReview(Boolean(res?.canCurate)); })
      .catch(() => { if (alive) setShowReview(false); });
    return () => { alive = false; };
  }, []);

  // Reconcile config-completeness for the visible registry-deployed networks (§14) so
  // tiles show green/yellow. Live-derived (no persisted flag); only on the Networks
  // tab, only for registry networks. Refetched when the network list changes.
  const refreshConfigStatus = useCallback(async (nid) => {
    try {
      const s = await getRegistryConfigStatus(nid);
      setConfigStatus((m) => ({ ...m, [nid]: s }));
    } catch {
      setConfigStatus((m) => ({ ...m, [nid]: { state: 'unknown' } }));
    }
  }, []);
  useEffect(() => {
    if (tab !== 'networks') return;
    const ids = networks.filter(isRegistryNetwork).map((n) => n.networkId);
    ids.forEach((nid) => { if (nid) refreshConfigStatus(nid); });
  }, [tab, networks, refreshConfigStatus]);

  const load = useCallback(async () => {
    // Networks tab is client-side (data from the shell via outletContext) — no fetch.
    if (networking) { setLoading(false); return; }
    setLoading(true);
    setLoadError(null);
    // Only send non-empty filters (empty = all).
    const active = Object.fromEntries(Object.entries(appliedFilters).filter(([, v]) => v.trim()));
    try {
      if (reviewing) {
        const res = await listRegistryDrafts(active);
        setDrafts(Array.isArray(res?.packages) ? res.packages : []);
      } else if (archiving) {
        const res = await listRegistryArchived(active);
        setArchived(Array.isArray(res?.packages) ? res.packages : []);
      } else {
        const res = await listRegistryPackages({});
        setPackages(Array.isArray(res?.packages) ? res.packages : []);
      }
    } catch (e) {
      setLoadError(e?.message || 'Failed to load');
      if (reviewing) setDrafts([]); else if (archiving) setArchived([]); else setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [reviewing, archiving, networking, appliedFilters]);

  useEffect(() => { load(); }, [load]);

  // Overlay the user's chosen version onto a package row (default = the tile's own
  // latest). The version/label override drives the badge, the detail page, and the
  // deploy wizard, so read+deploy all track the dropdown selection.
  const effectivePkg = useCallback((p) => {
    const pick = pickedVersion[p?.registryNetworkId];
    if (!pick) return p;
    return { ...p, version: pick.version, stepflowVersion: pick.stepflowVersion || p.stepflowVersion };
  }, [pickedVersion]);

  const entries = useMemo(() => {
    if (reviewing) return drafts.map(packageToEntry);
    if (archiving) return archived.map(packageToEntry);
    if (networking) {
      const filtered = networks.filter((n) => {
        if (sourceFilter === 'manual') return !isRegistryNetwork(n);
        if (sourceFilter === 'registry') return isRegistryNetwork(n);
        return true;
      });
      return filtered.map(networkToEntry);
    }
    const buildNew = {
      id: BUILD_NEW_ID,
      label: '+ Build new',
      description: 'Create a network from scratch in the workbench.',
      badge: 'Author',
    };
    return [buildNew, ...packages.map((p) => packageToEntry(effectivePkg(p)))];
  }, [reviewing, archiving, networking, drafts, archived, packages, networks, sourceFilter, effectivePkg]);

  // Tile click → open the STANDALONE detail page for that network (not the
  // CatalogBrowser master-detail). "Build new" jumps straight to the create flow.
  const onOpenEntry = useCallback((entry) => {
    if (!entry) return;
    if (entry.id === BUILD_NEW_ID) { navigate('/network/new'); return; }
    // Networks tab → open the network in the workbench. Registry tabs → standalone detail.
    if (entry._net) { navigate(`/network/${encodeURIComponent(entry._net.networkId)}`); return; }
    // Open the version the user picked on the tile (default = latest).
    setOpenTarget(entry._pkg ? effectivePkg(entry._pkg) : null);
  }, [navigate, effectivePkg]);

  // Detail-page primary action. Apps → open the deploy wizard. Review → approve
  // (release flip) → close the detail + refetch drafts (it drops off the list).
  const onPrimary = useCallback(async (p) => {
    if (!p?.registryNetworkId) return;
    if (reviewing) {
      if (!p.version) return;
      setApprovingId(p.registryNetworkId);
      setLoadError(null);
      try {
        await approveRegistryPackage(p.registryNetworkId, p.version);
        setOpenTarget(null);
        await load();
      } catch (e) {
        setLoadError(e?.message || 'Approve failed');
      } finally {
        setApprovingId('');
      }
      return;
    }
    setDeployTarget(p);
  }, [reviewing, load]);

  // Networks already deployed from the package the user is about to deploy — a pure
  // client-side scan of the in-memory network list (no API call). `sameVersion` marks
  // the exact-version copies (→ the "add a room instead" nudge in the deploy flow);
  // the rest are different-version deployments (informational only). Advisory, never
  // blocking — deploying a fresh independent network is always allowed.
  const existingDeployments = useMemo(() => {
    const rid = deployTarget?.registryNetworkId;
    if (!rid) return [];
    const wantVersion = Number(deployTarget?.version) || 0;
    return networks
      .filter((n) => n && n.sourceRegistryId === rid)
      .map((n) => ({
        networkId: n.networkId,
        name: n.name || n.networkId,
        version: Number(n.sourceRegistryVersion) || 0,
        stepflowVersion: n.sourceStepflowVersion || '',
        sameVersion: wantVersion > 0 && Number(n.sourceRegistryVersion) === wantVersion,
        _net: n,
      }));
  }, [deployTarget, networks]);

  // "Add a room instead" from the deploy flow's duplicate advisory → drop the deploy
  // flow and open the EXISTING create-room dialog against the already-deployed network.
  const onAddRoomInstead = useCallback((net) => {
    if (!net) return;
    setDeployTarget(null);
    setOpenTarget(null);
    setCreateRoomTarget(net);
  }, []);

  // Deploy flow finished (network created — with or without a room). Leave the
  // registry context entirely and land the user on the Networks tab with a FRESH
  // list, so the just-deployed network is visible with no hard refresh. We also
  // widen the source filter to 'all' so a bare (room-less) deployment isn't hidden
  // by the default 'active' (rooms>0) view.
  const onDeployFinished = useCallback(() => {
    setDeployTarget(null);
    setOpenTarget(null);       // close the standalone registry detail page, if open
    setTab('networks');
    setSourceFilter('all');
    if (refreshNetworks) refreshNetworks();
  }, [refreshNetworks]);

  // Shared runner for tile status-flip / delete actions: run fn, refetch, surface
  // errors. ``label`` names the action in any error message.
  const runTileAction = useCallback(async (p, label, fn) => {
    if (!p?.registryNetworkId || !p.version) return;
    setBusyId(p.registryNetworkId);
    setLoadError(null);
    try {
      await fn(p.registryNetworkId, p.version);
      await load();
    } catch (e) {
      setLoadError(e?.message || `${label} failed`);
    } finally {
      setBusyId('');
    }
  }, [load]);

  const onArchive = useCallback((p) => runTileAction(p, 'Archive', archiveRegistryPackage), [runTileAction]);
  const onRestoreToReview = useCallback((p) => runTileAction(p, 'Review', reviewRegistryPackage), [runTileAction]);
  // Delete is permanent → open an in-app confirm dialog (no window.confirm).
  // ``deleteTarget`` holds the package pending confirmation.
  const onDelete = useCallback((p) => {
    if (!p?.registryNetworkId || !p.version) return;
    setDeleteTarget(p);
  }, []);
  const confirmDelete = useCallback(async () => {
    const p = deleteTarget;
    if (!p) return;
    await runTileAction(p, 'Delete', deleteRegistryPackage);
    setDeleteTarget(null);
  }, [deleteTarget, runTileAction]);

  // Per-tile action dropdown, tab-specific (curators only — the Apps tab is
  // public, so its actions are gated on showReview = curation capability):
  //   Apps (RELEASED) → Archive, Delete
  //   Review (DRAFT)  → Archive, Delete
  //   Archived        → Review (restore), Delete
  const tileActions = useCallback((entry) => {
    const p = entry?._pkg;
    if (!p) return null;                 // e.g. the "Build new" tile
    if (!showReview) return null;        // only curators see any tile actions
    if (archiving) {
      return [
        { label: 'Review', testid: 'registry-tile-action-review', onClick: () => onRestoreToReview(p) },
        { label: 'Delete', testid: 'registry-tile-action-delete', danger: true, onClick: () => onDelete(p) },
      ];
    }
    // Review (DRAFT) and Apps (RELEASED) both offer Archive + Delete.
    return [
      { label: 'Archive', testid: 'registry-tile-action-archive', onClick: () => onArchive(p) },
      { label: 'Delete', testid: 'registry-tile-action-delete', danger: true, onClick: () => onDelete(p) },
    ];
  }, [showReview, archiving, onArchive, onRestoreToReview, onDelete]);

  // --- Networks tab: real-time upgrade detection + execution -----------------
  // Lazy check (on ⋮-open): compare the registry's LATEST released version to the
  // version this network was deployed from. NO eager fan-out over the whole list.
  const checkUpgrade = useCallback(async (n) => {
    const rid = n?.sourceRegistryId;
    if (!rid) return;
    setUpgradeInfo((m) => ({ ...m, [n.networkId]: { ...(m[n.networkId] || {}), checking: true } }));
    try {
      const res = await getRegistryPackage(rid);          // no version → latest RELEASED
      const pkg = res?.package || {};
      // Registry integers are the ordering key (never shown); stepflowVersion
      // ("1.00"/"1.10"/"2.00") is the ONLY version we render to the user.
      const latest = Number(pkg.version) || 0;
      const deployed = Number(n.sourceRegistryVersion) || 0;
      const latestLabel = pkg.stepflowVersion || '';
      const deployedLabel = n.sourceStepflowVersion || '';
      const available = String(pkg.status || '') === 'RELEASED' && latest > deployed;
      setUpgradeInfo((m) => ({ ...m, [n.networkId]: {
        checking: false, latestVersion: latest, latestLabel, available,
      } }));
      // User feedback — the menu closed on click, so a toast is the only signal.
      if (available) {
        const onClause = deployedLabel ? ` (you're on ${deployedLabel})` : '';
        showToast({ type: 'info', title: 'Upgrade available',
          message: `${n.name || n.networkId}: ${latestLabel} is available${onClause}.` });
      } else {
        showToast({ type: 'success', title: 'Up to date',
          message: `${n.name || n.networkId} is on the latest registry version.` });
      }
    } catch (_e) {
      // 404 / all-archived → no upgrade available.
      setUpgradeInfo((m) => ({ ...m, [n.networkId]: { checking: false, available: false } }));
      showToast({ type: 'error', title: 'Upgrade check failed',
        message: 'Could not reach the registry to check for a newer version.' });
    }
  }, [showToast]);

  const confirmUpgrade = useCallback(async () => {
    const n = upgradeTarget;
    if (!n?.networkId) return;
    setBusyId(n.networkId);
    setLoadError(null);
    try {
      await upgradeNetworkFromRegistry(n.networkId);   // server resolves latest RELEASED
      setUpgradeTarget(null);
      setUpgradeInfo((m) => ({ ...m, [n.networkId]: { checking: false, available: false } }));
      if (refreshNetworks) await refreshNetworks();
      showToast({ type: 'success', title: 'Network upgraded',
        message: `${n.name || n.networkId} upgraded. Cut a new release to roll it out to rooms.` });
    } catch (e) {
      setLoadError(e?.message || 'Upgrade failed');
      showToast({ type: 'error', title: 'Upgrade failed', message: e?.message || 'Upgrade failed' });
    } finally {
      setBusyId('');
    }
  }, [upgradeTarget, refreshNetworks, showToast]);

  // Per-tile actions for the Networks tab: Open (workbench); for registry-deployed
  // networks also the single-pane management actions — configure connectors/MCP,
  // create a room — plus a lazy "Check for upgrade" → "Upgrade" once newer.
  const networkTileActions = useCallback((entry) => {
    const n = entry?._net;
    if (!n) return null;
    const pinned = pinnedSet.has(n.networkId);
    const actions = [
      { label: 'Open', testid: 'networks-tile-action-open',
        onClick: () => navigate(`/network/${encodeURIComponent(n.networkId)}`) },
      // Personal ★ pin (per-device localStorage) — feeds the "Pinned" filter.
      { label: pinned ? 'Unpin from my view' : 'Pin to my view',
        testid: 'networks-tile-action-pin',
        onClick: () => togglePin(n.networkId) },
    ];
    if (isRegistryNetwork(n)) {
      const cfg = configStatus[n.networkId];
      const netMissing = Number(cfg?.network?.missing?.length || 0);
      actions.push({
        label: netMissing > 0 ? `MCP / Connector (${netMissing} missing)` : 'MCP / Connector',
        testid: 'networks-tile-action-tools',
        onClick: () => setToolConfigTarget(n),
      });
      actions.push({
        label: 'Create a room', testid: 'networks-tile-action-create-room',
        onClick: () => setCreateRoomTarget(n),
      });
      const info = upgradeInfo[n.networkId];
      if (info?.available) {
        actions.push({
          label: info.latestLabel ? `Upgrade to ${info.latestLabel}` : 'Upgrade',
          testid: 'networks-tile-action-upgrade',
          danger: false, onClick: () => setUpgradeTarget(n),
        });
      } else {
        actions.push({
          label: info?.checking ? 'Checking…' : 'Check for upgrade',
          testid: 'networks-tile-action-check-upgrade',
          onClick: () => checkUpgrade(n),
        });
      }
    }
    return actions;
  }, [navigate, upgradeInfo, checkUpgrade, configStatus, pinnedSet, togglePin]);

  // Dashboard-grid glue: the tile ⋮ menu reuses the SAME networkTileActions by
  // marrying a vitals row to its shell network object (which carries the registry
  // provenance the actions branch on). No shell row (rare) → Open-only.
  const renderDashActions = useCallback((vitals) => {
    const shell = networks.find((n) => n.networkId === vitals.networkId)
      || { networkId: vitals.networkId };
    const actions = networkTileActions({ _net: shell });
    return <TileActionsMenu actions={actions} testid={`networks-tile-actions-${vitals.networkId}`} />;
  }, [networks, networkTileActions]);

  const openNetworkFromVitals = useCallback((vitals) => {
    if (vitals?.networkId) navigate(`/network/${encodeURIComponent(vitals.networkId)}`);
  }, [navigate]);

  const openRoomFromVitals = useCallback((networkId, poolId) => {
    if (networkId && poolId) {
      navigate(`/agentic?networkId=${encodeURIComponent(networkId)}&poolId=${encodeURIComponent(poolId)}`);
    }
  }, [navigate]);

  // "View N executions" from the Rooms landing → open the room straight onto its
  // Executions tab (``&tab=executions`` deep-link, read by AgenticNetworkPage).
  const openRoomExecutions = useCallback((networkId, poolId) => {
    if (networkId && poolId) {
      navigate(`/agentic?networkId=${encodeURIComponent(networkId)}&poolId=${encodeURIComponent(poolId)}&tab=executions`);
    }
  }, [navigate]);

  // Networks-tab tile tone: green when config is complete, yellow when the network
  // still has connectors/MCPs to configure. Passed to the dashboard grid as a
  // {networkId -> 'green'|'yellow'} map (registry networks only; derived from the
  // live config-status reconcile).
  const configToneById = useMemo(() => {
    const m = {};
    for (const [nid, cfg] of Object.entries(configStatus || {})) {
      if (cfg?.state === 'yellow' || cfg?.state === 'green') m[nid] = cfg.state;
    }
    return m;
  }, [configStatus]);

  // Registry-tab tile control: a version dropdown (top-right of each tile) when the
  // network has published versions. Defaults to the tile's own (latest) version;
  // picking one re-renders the badge and flows into open/detail + deploy.
  const registryTileControl = useCallback((entry) => {
    const p = entry?._pkg;
    if (!p?.registryNetworkId) return null;   // e.g. the "Build new" tile
    return (
      <VersionSelect
        registryNetworkId={p.registryNetworkId}
        currentVersion={p.version}
        currentLabel={p.stepflowVersion}
        testidPrefix="registry"
        onPick={(v) => setPickedVersion((m) => ({
          ...m, [p.registryNetworkId]: { version: v.version, stepflowVersion: v.stepflowVersion },
        }))}
      />
    );
  }, []);

  const tabBtn = (id, label) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`-mb-px border-b-2 px-3 py-3 text-sm font-medium ${
        tab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
      data-testid={`registry-tab-${id}`}
    >
      {label}
    </button>
  );

  const count = reviewing ? drafts.length : archiving ? archived.length
    : networking ? entries.length : packages.length;
  const SOURCE_LABELS = {
    all: 'All networks',
    active: 'Active',        // rooms created (in use)
    deployed: 'Deployed',    // created, no rooms yet
    pinned: `Pinned${pinnedSet.size ? ` (${pinnedSet.size})` : ''}`,
    manual: 'Manually created',
    registry: 'Registry-deployed',
  };

  // Provenance filter fields for the Review/Archived tabs (label + query key).
  const FILTER_FIELDS = [
    ['sourceNetworkId', 'Network id'],
    ['tenantId', 'Tenant'],
    ['userId', 'User'],
    ['orgId', 'Org'],
    ['awsAccountId', 'AWS account'],
  ];
  const FILTER_LABELS = Object.fromEntries(FILTER_FIELDS.map(([k, l]) => [k, l]));
  // Chips reflect what's actually APPLIED (not the in-progress draft).
  const appliedFilterChips = FILTER_FIELDS
    .filter(([k]) => appliedFilters[k]?.trim())
    .map(([k]) => [k, appliedFilters[k]]);

  // Commit the draft filters (dropdown "Search" button).
  const applyFilters = useCallback(() => {
    setAppliedFilters(filters);
    setFilterMenuOpen(false);
  }, [filters]);

  // Clear both draft + applied (and the chips).
  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove a single applied filter chip (also clears the draft field).
  const removeFilter = useCallback((key) => {
    setFilters((f) => ({ ...f, [key]: '' }));
    setAppliedFilters((f) => ({ ...f, [key]: '' }));
  }, []);

  // Standalone detail page — the whole surface is the ONE selected network
  // (name + Comment/purpose + Agents/Skills/MCP/Connectors nav on the left,
  // selected item in the middle, ASL diagram on the right). Not a network list.
  if (openTarget) {
    const busy = busyId === openTarget.registryNetworkId || approvingId === openTarget.registryNetworkId;
    // Detail primary is tab-specific: Review→Approve, Archived→Review (restore),
    // Apps→Deploy.
    const primaryLabel = reviewing
      ? (busy ? 'Approving…' : 'Approve')
      : archiving
        ? (busy ? 'Restoring…' : 'Review')
        : 'Deploy';
    const onDetailPrimary = () => {
      if (archiving) { onRestoreToReview(openTarget).then(() => setOpenTarget(null)); return; }
      onPrimary(openTarget);
    };
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
          <button type="button" onClick={() => setOpenTarget(null)}
            data-testid="registry-detail-back"
            className="text-xs font-medium text-blue-600 hover:underline">← Back</button>
          <div className="flex items-center gap-2">
            {/* Version picker — only on the public Registry tab (Review/Archived show
                a single specific version). Changing it re-reads + re-deploys that version. */}
            {!curating && openTarget.registryNetworkId ? (
              <VersionSelect
                registryNetworkId={openTarget.registryNetworkId}
                currentVersion={openTarget.version}
                currentLabel={openTarget.stepflowVersion}
                testidPrefix="registry-detail"
                size="lg"
                onPick={(v) => {
                  setPickedVersion((m) => ({
                    ...m, [openTarget.registryNetworkId]: { version: v.version, stepflowVersion: v.stepflowVersion },
                  }));
                  setOpenTarget((cur) => (cur ? { ...cur, version: v.version, stepflowVersion: v.stepflowVersion } : cur));
                }}
              />
            ) : null}
            <button type="button" onClick={onDetailPrimary} disabled={busy}
              data-testid="registry-detail-primary"
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {primaryLabel}
            </button>
          </div>
        </div>
        {loadError ? (
          <div className="border-b border-red-100 bg-red-50 px-5 py-2 text-xs text-red-700">{loadError}</div>
        ) : null}
        <div className="min-h-0 flex-1">
          <PackageDetailView pkg={openTarget} />
        </div>
        {deployTarget ? (
          <NetworkDeployFlow
            open
            pkg={deployTarget}
            tenantId={activeTenantId}
            onClose={onDeployFinished}
            onNetworkReady={() => { if (refreshNetworks) refreshNetworks(); }}
            existingDeployments={existingDeployments}
            onAddRoom={onAddRoomInstead}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col px-6 lg:px-10">
      {/* Top band — AWS-console-style "recently visited" (left) + a reserved
          slot (right). Shown above the tab bar on both Apps and Review. */}
      <div className="shrink-0 py-4">
        <RecentlyVisited />
      </div>
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5">
        <div className="flex items-end gap-1">
          {tabBtn('networks', 'Networks')}
          {/* Rooms landing — shown only when the caller belongs to any room. This is
              the room-scoped overview for room owners/supervisors/viewers (who may not
              be network members); "View N executions" per tile. */}
          {agenticRooms.length ? tabBtn('rooms', 'Rooms') : null}
          {tabBtn('registry', 'Registry')}
        </div>
        <div className="flex items-center gap-2">
          {/* Curation tabs (dev + ADMIN) moved to the right, LEFT of the dropdown.
              Own items-end sub-group so their underline sits on the border line. */}
          {showReview ? (
            <div className="flex items-end gap-1 self-stretch">
              {tabBtn('review', 'Review')}
              {tabBtn('archived', 'Archived')}
            </div>
          ) : null}
          {/* Networks tab: source selector (All / Active / Deployed / …). */}
          {networking ? (
            <>
              <button
                type="button"
                ref={sourceBtnRef}
                onClick={() => setSourceMenuOpen((v) => !v)}
                data-testid="networks-source-toggle"
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
              >
                {SOURCE_LABELS[sourceFilter]}
                <ChevronDown className="h-3.5 w-3.5" aria-hidden />
              </button>
              <PortalMenu anchorRef={sourceBtnRef} open={sourceMenuOpen}
                onClose={() => setSourceMenuOpen(false)} align="bottom-right" width={180}>
                <div className="py-1" data-testid="networks-source-menu">
                  {Object.entries(SOURCE_LABELS).map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => { setSourceFilter(val); setSourceMenuOpen(false); }}
                      data-testid={`networks-source-${val}`}
                      className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 ${
                        sourceFilter === val ? 'font-medium text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </PortalMenu>
            </>
          ) : null}
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" aria-hidden />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              data-testid="registry-search"
              className="w-56 rounded-md border border-gray-200 py-1.5 pl-7 pr-2 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
            />
          </div>
          {/* Advanced search (Review/Archived): an icon-only trigger that opens a
              dropdown of provenance filters; committed via its Search button.
              Uses PortalMenu so it isn't clipped by the tab-panel overflow. */}
          {curating ? (
            <>
              <button
                type="button"
                ref={filterBtnRef}
                onClick={() => setFilterMenuOpen((v) => !v)}
                aria-label="Advanced search"
                title="Advanced search"
                data-testid="drafts-filter-toggle"
                className={`inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors ${
                  appliedFilterChips.length
                    ? 'border-blue-300 bg-blue-50 text-blue-600'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                }`}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
              </button>
              <PortalMenu
                anchorRef={filterBtnRef}
                open={filterMenuOpen}
                onClose={() => setFilterMenuOpen(false)}
                align="bottom-right"
                width={280}
                className="rounded-lg border border-gray-200 bg-white shadow-xl"
              >
                <div className="p-3" data-testid="drafts-filter-menu">
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Advanced search
                  </div>
                  <div className="flex flex-col gap-2">
                    {FILTER_FIELDS.map(([key, label]) => (
                      <label key={key} className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</span>
                        <input
                          type="text"
                          value={filters[key]}
                          onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                          placeholder="all"
                          data-testid={`drafts-filter-${key}`}
                          className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                        />
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={clearFilters}
                      data-testid="drafts-filter-clear"
                      className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={applyFilters}
                      data-testid="drafts-filter-apply"
                      className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                    >
                      <Search className="h-3 w-3" aria-hidden /> Search
                    </button>
                  </div>
                </div>
              </PortalMenu>
            </>
          ) : null}
        </div>
      </div>
      {curating && appliedFilterChips.length ? (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-100 bg-gray-50/60 px-5 py-1.5" data-testid="drafts-filter-chips">
          {appliedFilterChips.map(([key, value]) => (
            <span
              key={key}
              className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] text-blue-900"
            >
              <span className="font-medium">{FILTER_LABELS[key]}:</span> {value}
              <button
                type="button"
                onClick={() => removeFilter(key)}
                aria-label={`Remove ${FILTER_LABELS[key]} filter`}
                data-testid={`drafts-filter-chip-remove-${key}`}
                className="text-blue-700 hover:text-blue-900"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-md px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-100"
          >
            Clear all
          </button>
        </div>
      ) : null}
      {loadError ? (
        <div className="border-b border-red-100 bg-red-50 px-5 py-2 text-xs text-red-700">
          {loadError}{' '}
          <button type="button" onClick={load} className="underline">Retry</button>
        </div>
      ) : null}
      {/* Networks + Rooms tabs OWN a fixed-height flex column (header + scrollable
          grid + pinned footer) so the fleet footer never scrolls. Other tabs keep the
          shared overflow-y-auto scroll. */}
      <div className={`min-h-0 flex-1 ${networking || rooming ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        {rooming ? (
          <div className="flex h-full min-h-0 flex-col px-5 pt-4">
            <div className="mb-3 flex shrink-0 items-baseline justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">My rooms</h2>
                <p className="text-[11px] text-gray-500">
                  {roomVitalsLoading && roomVitals.length === 0
                    ? 'Loading…'
                    : `${agenticRooms.length} room${agenticRooms.length === 1 ? '' : 's'} you belong to`}
                </p>
              </div>
            </div>
            <RoomLandingGrid
              rooms={roomVitals}
              loading={roomVitalsLoading}
              error={roomVitalsError}
              query={search}
              onOpenRoom={openRoomFromVitals}
              onOpenExecutions={openRoomExecutions}
              emptyHint="You’re not a member of any rooms yet."
            />
          </div>
        ) : networking ? (
          <div className="flex h-full min-h-0 flex-col px-5 pt-4">
            <div className="mb-3 flex shrink-0 items-baseline justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Networks</h2>
                <p className="text-[11px] text-gray-500">
                  {dashLoading && dashVitalsFiltered.length === 0
                    ? 'Loading…'
                    : `${dashVitalsFiltered.length} network${dashVitalsFiltered.length === 1 ? '' : 's'} · live`}
                </p>
              </div>
            </div>
            <NetworkDashboardGrid
              networks={dashVitalsFiltered}
              loading={dashLoading}
              error={dashError}
              query={search}
              livePing={livePing}
              toneById={configToneById}
              lifecycleFilter={sourceFilter}
              onBuildNew={() => navigate('/network/new')}
              onOpenNetwork={openNetworkFromVitals}
              onOpenRoom={openRoomFromVitals}
              renderActions={renderDashActions}
              emptyHint={sourceFilter === 'pinned'
                ? 'No pinned networks. Use a tile’s ⋮ menu → “Pin to my view” to add one here.'
                : sourceFilter === 'deployed'
                  ? 'No deployed networks without rooms.'
                  : undefined}
            />
          </div>
        ) : (
          <CatalogBrowser
            open
            inline
            title={reviewing ? 'Review — pending approval' : archiving ? 'Archived' : 'Registry'}
            subtitle={
              loading
                ? 'Loading…'
                : reviewing
                  ? `${count} draft${count === 1 ? '' : 's'} awaiting approval`
                  : archiving
                    ? `${count} archived package${count === 1 ? '' : 's'}`
                    : `${count} released network${count === 1 ? '' : 's'}`
            }
            entries={entries}
            loading={loading}
            onOpenEntry={onOpenEntry}
            tileActions={showReview ? tileActions : null}
            tileControl={(!reviewing && !archiving) ? registryTileControl : null}
            tileTone={null}
            renderLeading={renderRegistryLeading}
            onCancel={() => {}}
            testidPrefix="registry"
            query={search}
            onQueryChange={setSearch}
            hideSearchBar
          />
        )}
      </div>
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete from registry?"
        message={deleteTarget
          ? `Permanently delete "${deleteTarget.title || deleteTarget.registryNetworkId}" (${deleteTarget.stepflowVersion || `v${deleteTarget.version}`}).\n\nThis removes it from the registry — the database entry and the stored package — and cannot be undone.`
          : ''}
        confirmLabel="Delete"
        danger
        busy={!!deleteTarget && busyId === deleteTarget.registryNetworkId}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        testid="registry-delete-confirm"
      />
      <ConfirmDialog
        open={!!upgradeTarget}
        title="Upgrade network from registry?"
        message={upgradeTarget
          ? `Upgrade "${upgradeTarget.name || upgradeTarget.networkId}" to ${upgradeInfo[upgradeTarget.networkId]?.latestLabel || 'the latest version'}.\n\nThis imports the newer registry version as a new release of this network (agents, graph and skills are replaced from the new version). Your rooms stay on their current release until you move them.`
          : ''}
        confirmLabel="Upgrade"
        busy={!!upgradeTarget && busyId === upgradeTarget.networkId}
        onConfirm={confirmUpgrade}
        onCancel={() => setUpgradeTarget(null)}
        testid="networks-upgrade-confirm"
      />
      {/* Deploy from a Registry tile → the single-pane flow (network → tools → rooms). */}
      {deployTarget && !openTarget ? (
        <NetworkDeployFlow
          open
          pkg={deployTarget}
          tenantId={activeTenantId}
          onClose={onDeployFinished}
          onNetworkReady={() => { if (refreshNetworks) refreshNetworks(); }}
          existingDeployments={existingDeployments}
          onAddRoom={onAddRoomInstead}
        />
      ) : null}
      {/* Networks-tab ⋮ "MCP / Connector" → network-scope config against the live network. */}
      {toolConfigTarget ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button type="button" aria-label="Close"
            className="absolute inset-0 bg-black/40"
            onClick={() => { setToolConfigTarget(null); refreshConfigStatus(toolConfigTarget.networkId); }} />
          <div role="dialog" aria-modal="true"
            className="relative z-[101] flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
            data-testid="networks-tool-config">
            <h2 className="text-lg font-semibold text-gray-900">
              Connect “{toolConfigTarget.name || toolConfigTarget.networkId}”
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Network-level connections. Configure any that are still missing — pick an
              existing one or create a new one.
            </p>
            <div className="mt-3 min-h-0 flex-1">
              <NetworkToolConfigPanel
                pkg={{
                  registryNetworkId: toolConfigTarget.sourceRegistryId,
                  version: toolConfigTarget.sourceRegistryVersion,
                }}
                networkId={toolConfigTarget.networkId}
                scope="network"
                tenantId={activeTenantId}
                doneLabel="Done"
                onDone={() => { setToolConfigTarget(null); refreshConfigStatus(toolConfigTarget.networkId); }}
              />
            </div>
          </div>
        </div>
      ) : null}
      {/* Networks-tab ⋮ "Create a room" → the EXISTING release dialog for this network. */}
      {createRoomTarget ? (
        <ReleaseNetworkDialog
          open
          networkId={createRoomTarget.networkId}
          networkName={createRoomTarget.name || createRoomTarget.networkId}
          network={createRoomTarget}
          onClose={() => setCreateRoomTarget(null)}
          onReleased={() => {
            const nid = createRoomTarget.networkId;
            setCreateRoomTarget(null);
            if (refreshNetworks) refreshNetworks();
            refreshConfigStatus(nid);
          }}
        />
      ) : null}
    </div>
  );
}
